import type { LimitesDuSpot, SiroccoEvent } from '@sirocco/shared';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { events } from '../../../api/endpoints';
import { retenirLaVue, vueRetenue } from './memoire';
import {
  PAR_DEFAUT,
  pastilleDe,
  pastilleDeGroupe,
  mettreLesPunaisesALEchelle,
  rondDeMoi,
  type Lieu,
  type ProprietesDeCarte,
  type SortiePlacee,
} from './moteur';
import { langue, t } from '../../../i18n';

/**
 * La carte Apple : MapKit JS, le rendu d'Apple Plans.
 *
 * Le meme dessin, les memes noms de lieux et le meme mode sombre que
 * l'application Plans de l'iPhone, sur le web et sur Android aussi. Elle
 * s'ouvre avec un jeton que le serveur signe a la demande (voir
 * `functions/src/carte.ts`) ; la bibliotheque se charge depuis le serveur
 * d'Apple, seulement quand on ouvre la carte.
 *
 * Rien de ce qu'Apple propose pour localiser le membre n'est utilise :
 * elle demanderait la position par la page, avec sa propre question, alors
 * que l'application l'a deja par le telephone. On pose notre rond.
 */

/* Ce qu'on utilise de MapKit, decrit juste assez pour le typer. */
interface Coordonnee {
  latitude: number;
  longitude: number;
}
export interface Annotation {
  addEventListener(type: 'select', ecouteur: () => void): void;
  /** Ou il est pose : de quoi savoir si mon point a bouge. */
  coordinate?: Coordonnee;
  /** Ce que la pastille represente, pour le retrouver dans un groupe. */
  data?: { sortie?: SiroccoEvent };
}

interface Groupe {
  coordinate: Coordonnee;
  memberAnnotations: Annotation[];
}
export interface CarteMapKit {
  colorScheme: string;
  /** Ce qu'on dessine a la place de plusieurs pastilles superposees. */
  annotationForCluster?: (groupe: Groupe) => Annotation;
  region: { center: Coordonnee; span: { latitudeDelta: number; longitudeDelta: number } } | null;
  selectedAnnotation: Annotation | null;
  addAnnotation(annotation: Annotation): void;
  addAnnotations(annotations: Annotation[]): void;
  removeAnnotation(annotation: Annotation): void;
  removeAnnotations(annotations: Annotation[]): void;
  setRegionAnimated(region: unknown, anime?: boolean): void;
  setRotationAnimated(degres: number, anime?: boolean): void;
  convertCoordinateToPointOnPage(coordonnee: Coordonnee): DOMPoint;
  addEventListener(type: string, ecouteur: (evenement: unknown) => void): void;
  destroy(): void;
}
/** Un lieu tel qu'Apple le decrit. */
export interface PlaceMapKit {
  id?: string;
  name: string;
  coordinate: Coordonnee;
  formattedAddress?: string;
  telephone?: string;
  urls?: string[];
  pointOfInterestCategory?: string;
}
type ReponseDeRecherche = (erreur: unknown, reponse: { places: PlaceMapKit[] }) => void;

export interface MapKit {
  init(options: { authorizationCallback: (fini: (jeton: string) => void) => void; language: string }): void;
  addEventListener(type: 'error', ecouteur: (evenement: { status?: string }) => void): void;
  Map: {
    new (element: HTMLElement, options: Record<string, unknown>): CarteMapKit;
    ColorSchemes: { Light: string; Dark: string };
    MapTypes: { Hybrid: string };
    LoadPriorities: { LandCover: string; PointsOfInterest: string; None: string };
  };
  Coordinate: new (latitude: number, longitude: number) => Coordonnee;
  CoordinateSpan: new (latitudeDelta: number, longitudeDelta: number) => unknown;
  CoordinateRegion: new (centre: Coordonnee, etendue: unknown) => unknown;
  CameraZoomRange: new (distanceMin: number, distanceMax: number) => unknown;
  Annotation: new (
    coordonnee: Coordonnee,
    fabrique: () => Element,
    options: Record<string, unknown>,
  ) => Annotation;
  FeatureVisibility: { Hidden: string; Adaptive: string };
  Padding: new (haut: number, droite: number, bas: number, gauche: number) => unknown;
  PointOfInterestCategory: Record<string, unknown>;
  PointOfInterestFilter: { including(categories: unknown[]): unknown; excludingAllCategories: unknown };
  PointsOfInterestSearch: new (options: Record<string, unknown>) => {
    search(reponse: ReponseDeRecherche): void;
  };
  Search: (new (options: Record<string, unknown>) => {
    search(texte: string, reponse: ReponseDeRecherche): void;
  }) & { RegionPriority: { Required: unknown } };
}

const BIBLIOTHEQUE = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js';
/** L'etendue d'une vue, en degres : un quartier autour de moi, la ville sinon. */
const ETENDUE_MOI = 0.014;
const ETENDUE_VILLE = 0.25;
const ETENDUE_FRANCE = 9;
/*
  Le plus pres ou l'on se pose : environ deux cents metres de large, la
  rue et ce qui la borde.

  C'est la ou mene un point qu'on touche, seul ou groupe. S'arreter au
  quartier laissait deviner l'endroit sans le montrer : on voyait dans
  quel coin de la ville ca se passe, pas devant quelle porte.
*/
const ETENDUE_PRES = 0.002;
/*
  Se rapprocher d'un groupe : sa largeur, et de la marge autour.

  Le facteur fait le travail — a deux fois et demie leur ecart, deux
  points occupent moins de la moitie de l'ecran et se retrouvent a cent
  cinquante pixels l'un de l'autre, bien au-dela du rayon qui les avait
  rassembles.
*/
const MARGE_GROUPE = 2.6;

/**
 * Sur quoi la carte s'ouvre.
 *
 * Ma position si on me l'a donnee et que je suis dans la ville : on
 * arrive chez soi, pas sur une vue d'ensemble. Sinon la ville, telle
 * qu'elle est, sans se laisser etirer par une sortie a l'autre bout du
 * pays — celle-la, on ira la chercher.
 */
function surQuoiSOuvrir(actuel: ProprietesDeCarte): 'moi' | 'ville' | 'rien' {
  if (actuel.moi && actuel.moiDansLaVille) return 'moi';
  if (actuel.centreVille) return 'ville';
  return 'rien';
}

/**
 * Le territoire du spot, dit a MapKit.
 *
 * Deux reglages, parce que MapKit separe les deux gestes : le centre de la
 * vue ne sort pas de la boite (`cameraBoundary`), et la camera ne monte
 * pas plus haut qu'il ne faut pour la voir en entier (`cameraZoomRange`).
 * Sans le second, on resterait centre sur la France en voyant l'Europe.
 */
export function bornes(
  mapkit: MapKit,
  limites: Pick<LimitesDuSpot, 'nord' | 'sud' | 'est' | 'ouest'> | undefined,
): Record<string, unknown> {
  if (!limites) return {};
  const hauteur = limites.nord - limites.sud;
  return {
    cameraBoundary: new mapkit.CoordinateRegion(
      new mapkit.Coordinate((limites.sud + limites.nord) / 2, (limites.ouest + limites.est) / 2),
      new mapkit.CoordinateSpan(hauteur, limites.est - limites.ouest),
    ),
    // Un degre de latitude fait 111 km ; on recule d'un peu plus que la hauteur du pays.
    cameraZoomRange: new mapkit.CameraZoomRange(0, hauteur * 111_000 * 1.6),
  };
}

let chargement: Promise<MapKit> | null = null;
let pannes: ((message: string) => void)[] = [];

/**
 * Etre prevenu quand Apple refuse : un jeton refuse ne fait pas echouer le
 * chargement de la bibliotheque, il arrive apres. Rend de quoi se retirer.
 */
export function ecouterLesPannes(signaler: (message: string) => void): () => void {
  pannes.push(signaler);
  return () => {
    pannes = pannes.filter((autre) => autre !== signaler);
  };
}

/**
 * Charge MapKit une fois pour toute la session, et l'initialise.
 *
 * Le jeton est redemande par MapKit lui-meme quand il expire : la fonction
 * d'autorisation reste branchee sur le serveur.
 */
export function chargerMapKit(): Promise<MapKit> {
  chargement ??= new Promise<MapKit>((resoudre, rejeter) => {
    const fenetre = window as unknown as { mapkit?: MapKit; __siroccoMapKit?: () => void };
    fenetre.__siroccoMapKit = () => {
      const mapkit = fenetre.mapkit;
      if (!mapkit) return rejeter(new Error('MapKit absent'));
      mapkit.init({
        language: langue,
        authorizationCallback: (fini) => {
          void events
            .jetonDeCarte()
            .then(fini)
            .catch(() => pannes.forEach((signaler) => signaler(t.carte.apple.pasOuverte)));
        },
      });
      mapkit.addEventListener('error', () =>
        pannes.forEach((signaler) => signaler(t.carte.apple.pasOuverte)),
      );
      resoudre(mapkit);
    };
    const script = document.createElement('script');
    script.src = BIBLIOTHEQUE;
    script.crossOrigin = 'anonymous';
    script.async = true;
    /* `services` : les recherches de lieux de l'onglet Lieux. */
    script.dataset.libraries = 'map,annotations,services';
    script.dataset.callback = '__siroccoMapKit';
    script.onerror = () => {
      chargement = null;
      script.remove();
      rejeter(new Error('MapKit injoignable'));
    };
    document.head.appendChild(script);
  });
  return chargement;
}

/**
 * Va chercher la bibliotheque et le jeton d'avance.
 *
 * Tout se paie au premier affichage : quelques centaines de kilooctets
 * chez Apple, et un aller-retour jusqu'a la fonction qui signe le jeton,
 * qui peut demarrer a froid. L'onglet des sorties appelle donc ceci en
 * arrivant — on est a un doigt de la carte, autant que l'attente soit
 * passee avant le geste.
 */
export function prechargerLaCarte(): void {
  void chargerMapKit().catch(() => {
    /* Rien a dire : l'ecran de la carte reessaiera et parlera pour lui. */
  });
}

export function CarteApple(props: ProprietesDeCarte) {
  const { sorties, moi, centreVille, nuit, onChoisir, onPanne, poignee } = props;
  const zone = useRef<HTMLDivElement>(null);
  const carte = useRef<{ mapkit: MapKit; carte: CarteMapKit } | null>(null);
  const reperes = useRef<Annotation[]>([]);
  const monRepere = useRef<Annotation | null>(null);
  const cadres = useRef<ResizeObserver | null>(null);
  /* De quoi retirer les ecouteurs poses sur le fond a la creation. */
  const retirerLeDoigt = useRef<(() => void) | null>(null);
  /* Redessiner les reperes depuis un ecouteur pose a la creation. */
  const reposer = useRef<(() => void) | null>(null);
  const redessin = useRef<number | null>(null);
  /* Ce que montrent les pastilles en ce moment : inutile de les refaire
     tant que les memes sorties tombent dans les memes groupes. */
  const dessine = useRef('');
  /*
    Ce qu'on a deja annonce comme visible : on ne le redit pas.

    Le repere de depart n'est pas la chaine vide : c'est ce que produit
    une vue sans aucune sortie, et une carte ouverte loin de tout
    n'aurait alors jamais annonce qu'elle etait vide.
  */
  const vueDite = useRef('?');
  /*
    Quand une pastille a ete touchee pour la derniere fois.

    Un appui sur une pastille est aussi un appui sur la carte : MapKit
    reconnait le geste sur son conteneur, et son `single-tap` arrivait
    juste apres l'ouverture de la fiche pour la refermer aussitot. On la
    voyait paraitre et disparaitre. `stopPropagation` sur le clic du
    bouton n'y peut rien — ce n'est pas le meme chemin.
  */
  const ouvertA = useRef(0);
  /*
    Les cadrages automatiques se posent, ils ne volent pas.

    Un vol traverse tous les zooms intermediaires, et chacun demande ses
    tuiles a Apple : la carte partait vers les sorties en chargeant tout
    ce qu'il y a entre les deux. Seul un geste voulu — viser une sortie
    choisie — merite une animation.
  */
  /* Sur quoi la vue s'est posee toute seule. La position, quand elle
     arrive, remplace la ville ; l'inverse n'arrive jamais. */
  const cadrageFait = useRef<'moi' | 'ville' | 'rien'>('rien');
  /* Le membre a pris la main : on ne recadre plus sous son doigt. */
  const pilotee = useRef(false);
  /*
    Nos propres mouvements ne sont pas des gestes.

    MapKit annonce `scroll-start` et `zoom-start` aussi bien pour un doigt
    que pour une animation qu'on a lancee, et rien dans l'evenement ne les
    distingue. Le premier cadrage passait donc pour une prise en main :
    la carte se figeait sur la ville et n'allait plus jamais sur la
    position, qui arrive une seconde plus tard.
  */
  const nous = useRef(0);
  const bouger = (action: () => void) => {
    nous.current += 1;
    action();
    window.setTimeout(() => {
      nous.current -= 1;
    }, 900);
  };
  const derniers = useRef(props);
  derniers.current = props;
  const choisir = useRef(onChoisir);
  choisir.current = onChoisir;
  const grouperLes = useRef(props.onGroupe);
  grouperLes.current = props.onGroupe;

  /*
    Ou s'ouvrir, calcule ici plutot que demande a la carte apres coup :
    en le sachant avant de la creer, il n'y a qu'un seul endroit a
    dessiner, donc une seule serie de tuiles a charger.

    Les sorties n'entrent pas dans ce calcul. La vue les englobait
    toutes, et une seule sortie a l'autre bout du pays suffisait a
    reculer jusqu'a ne plus rien reconnaitre : on ouvrait la carte de sa
    ville et l'on voyait une region. C'est la ville qu'on vient voir, ou
    soi-meme.
  */
  const regionDeDepart = (mapkit: MapKit, actuel: ProprietesDeCarte) => {
    const quoi = surQuoiSOuvrir(actuel);
    if (quoi === 'moi') return region(mapkit, actuel.moi!, ETENDUE_MOI);
    if (quoi === 'ville') {
      return region(mapkit, actuel.centreVille!, actuel.etendueVille ?? ETENDUE_VILLE);
    }
    return region(mapkit, PAR_DEFAUT, ETENDUE_FRANCE);
  };

  const region = (mapkit: MapKit, point: Lieu, etendue: number) =>
    new mapkit.CoordinateRegion(
      new mapkit.Coordinate(point.lat, point.lon),
      new mapkit.CoordinateSpan(etendue, etendue),
    );

  /* La carte, des l'arrivee. */
  useEffect(() => {
    let vivant = true;
    const signaler = (message: string) => vivant && onPanne(message);
    pannes.push(signaler);

    void chargerMapKit()
      .then((mapkit) => {
        if (!vivant || !zone.current) return;
        /* On revient d'ailleurs : la carte reprend ou on l'avait laissee,
           et ne se recadre plus toute seule. */
        const gardee = vueRetenue();
        if (gardee) pilotee.current = true;
        const instance = new mapkit.Map(zone.current, {
          colorScheme: derniers.current.nuit ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light,
          /*
            La carte tourne a deux doigts, et la boussole ramene au nord.

            Elle etait bloquee pour qu'on ne perde pas le nord sans le
            vouloir. Mais on tourne une carte pour aligner ce qu'on voit
            sur ce qu'on a devant soi, et c'est justement ce qu'on fait en
            arrivant quelque part. La boussole parait des que le nord
            bouge, et un appui dessus le retrouve : le risque disparait
            avec elle.
          */
          isRotationEnabled: true,
          showsCompass: mapkit.FeatureVisibility.Adaptive,
          showsScale: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsUserLocationControl: false,
          /* Rien a regler ici : les priorites de chargement d'Apple font
             mieux que ce qu'on a essaye de leur imposer. Couper les
             points d'interet ou reclamer les terres d'abord rendait la
             carte plus longue a se dessiner, pas moins. */
          region: gardee
            ? region(mapkit, gardee, gardee.etendue ?? ETENDUE_MOI)
            : regionDeDepart(mapkit, derniers.current),
          ...bornes(mapkit, derniers.current.limites),
        });
        /* Un geste du membre : on ne le recadre plus. */
        const prendLaMain = () => {
          if (nous.current !== 0) return;
          pilotee.current = true;
          /* Le second chemin vers le repli du tiroir : voir `auDoigt`
             plus bas. Celui-ci ne se declenche pas toujours, celui-la
             si — les deux ensemble ne manquent rien, et se replier deux
             fois ne coute rien. */
          derniers.current.onGeste();
        };
        instance.addEventListener('scroll-start', prendLaMain);
        instance.addEventListener('zoom-start', prendLaMain);
        /* Ce qu'on voit maintenant, pour le retrouver au retour. */
        instance.addEventListener('region-change-end', () => {
          /*
            La vue a change : ce qui se touchait peut se separer, et
            l'inverse. On redessine les reperes, jamais le cadrage.

            Hors de l'evenement, et une seule fois : retirer et reposer
            des pastilles pendant que MapKit met sa vue a jour le laisse
            avec des elements orphelins, sans position ni taille.
          */
          if (redessin.current) window.clearTimeout(redessin.current);
          redessin.current = window.setTimeout(() => reposer.current?.(), 150);
          const vue = instance.region;
          if (!vue) return;
          retenirLaVue({
            lat: vue.center.latitude,
            lon: vue.center.longitude,
            etendue: vue.span.latitudeDelta,
          });
          echelonner(vue.span.latitudeDelta);
        });
        echelonner(instance.region?.span.latitudeDelta);
        /* Toucher le fond referme la fiche ouverte — mais pas celle que
           le meme appui vient d'ouvrir : voir `ouvertA`. */
        instance.addEventListener('single-tap', () => {
          if (Date.now() - ouvertA.current < 400) return;
          choisir.current(null);
        });

        /* Le regroupement se calcule dans `poser` : celui de MapKit
           rassemble bien, mais n'affiche pas la pastille qu'on lui donne
           en echange. */
        carte.current = { mapkit, carte: instance };
        /* La carte s'est ouverte sur ce qu'il fallait voir : pas de
           recadrage dans la foulee, donc pas de seconde serie de tuiles. */
        cadrageFait.current = gardee ? 'moi' : surQuoiSOuvrir(derniers.current);

        /*
          MapKit se retaille sur la fenetre, pas sur son cadre.

          Il mesure son conteneur une fois, a la creation, puis n'ecoute
          plus que `resize` de la fenetre. Un cadre qui grandit apres coup
          — la barre d'etat qui se pose, le clavier qui se retire, un
          retour depuis une autre page — le laissait a son ancienne
          hauteur : la carte s'arretait en pleine page et le fond de
          l'ecran apparaissait dessous. On lui rejoue donc l'evenement
          qu'il attend des que le cadre bouge.
        */
        /*
          Un doigt sur la carte replie le tiroir, et c'est le DOM qui le
          dit.

          On l'avait demande a MapKit — `scroll-start`, `zoom-start` —,
          et il ne les annonce pas toujours : le tiroir se repliait une
          fois sur deux, selon le geste. Un `pointerdown` sur le fond,
          lui, precede forcement tout pincement, tout glissement et tout
          appui. En capture, pour que rien ne puisse le retenir ; et nos
          propres deplacements n'en produisent aucun, donc la carte qui
          vole vers une sortie ne replie rien.
        */
        const fond = zone.current;
        const auDoigt = () => derniers.current.onGeste();
        fond.addEventListener('pointerdown', auDoigt, { passive: true, capture: true });
        /* Et la molette, pour qui regarde depuis un ordinateur. */
        fond.addEventListener('wheel', auDoigt, { passive: true, capture: true });
        retirerLeDoigt.current = () => {
          fond.removeEventListener('pointerdown', auDoigt, { capture: true });
          fond.removeEventListener('wheel', auDoigt, { capture: true });
        };

        const cadre = new ResizeObserver(() => window.dispatchEvent(new Event('resize')));
        cadre.observe(zone.current);
        cadres.current = cadre;
        /* Une premiere fois, apres la mise en page : la carte vient
           d'etre posee dans un cadre qui n'avait pas fini de s'etablir. */
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));

        poser();
      })
      .catch(() => signaler(t.carte.apple.pasChargee));

    return () => {
      vivant = false;
      if (redessin.current) window.clearTimeout(redessin.current);
      retirerLeDoigt.current?.();
      retirerLeDoigt.current = null;
      cadres.current?.disconnect();
      cadres.current = null;
      pannes = pannes.filter((autre) => autre !== signaler);
      carte.current?.carte.destroy();
      carte.current = null;
      reperes.current = [];
      monRepere.current = null;
    };
    // Creee une fois : le reste la suit par `poser`.
  }, []);

  /*
    Rassembler ce qui se superpose, a l'ecran et non sur la carte.

    Deux sorties a la meme adresse ne font qu'un point ; deux sorties a
    trois cents metres en font un ou deux selon le zoom. Ce qui compte
    est la distance sous le doigt, pas celle sur le terrain : on convertit
    donc chaque sortie en pixels avant de grouper, et l'on recommence des
    que la vue change.

    MapKit sait le faire seul — `clusteringIdentifier` —, mais il n'a
    jamais affiche la pastille qu'on lui rendait en echange : essaye et
    abandonne, celui-ci se verifie.
  */
  /* Deux punaises fusionnent quand leurs tetes se touchent, pas avant. */
  const RAYON_PX = 22;

  /*
    La taille des punaises, a la fin de chaque mouvement : pleine sur un
    quartier, moitie quand on embrasse la ville. MapKit ne dit sa vue qu'a
    la fin d'un geste ; la punaise s'ajuste a ce moment-la.
  */
  function echelonner(etendue: number | undefined) {
    if (!etendue) return;
    mettreLesPunaisesALEchelle(zone.current, Math.sqrt(ETENDUE_MOI / etendue));
  }

  function grouper(actuel: ProprietesDeCarte): { lieu: Lieu; sorties: SortiePlacee[] }[] {
    const vue = carte.current?.carte.region;
    const cadre = zone.current?.getBoundingClientRect();
    if (!vue || !cadre || cadre.width === 0) {
      return actuel.sorties.map((sortie) => ({ lieu: sortie, sorties: [sortie] }));
    }

    const parPixelX = vue.span.longitudeDelta / cadre.width;
    const parPixelY = vue.span.latitudeDelta / cadre.height;
    const groupes: { lieu: Lieu; sorties: SortiePlacee[]; x: number; y: number }[] = [];

    for (const sortie of actuel.sorties) {
      const x = (sortie.lon - vue.center.longitude) / parPixelX;
      const y = (vue.center.latitude - sortie.lat) / parPixelY;
      const proche = groupes.find((g) => Math.hypot(g.x - x, g.y - y) < RAYON_PX);
      if (proche) proche.sorties.push(sortie);
      else groupes.push({ lieu: sortie, sorties: [sortie], x, y });
    }
    return groupes.map(({ lieu, sorties: dedans }) => ({ lieu, sorties: dedans }));
  }

  /**
   * Ce qu'on voit en ce moment, annonce a l'ecran.
   *
   * La region que MapKit donne est un rectangle nord-sud : une carte
   * tournee la deborde un peu. C'est sans consequence ici — un evenement
   * compte de trop dans un coin vaut mieux qu'un compte qui saute des
   * qu'on incline la carte.
   */
  function dire(actuel: ProprietesDeCarte) {
    const vue = carte.current?.carte.region;
    if (!vue) return;
    const ids = actuel.sorties
      .filter(
        (sortie) =>
          Math.abs(sortie.lat - vue.center.latitude) <= vue.span.latitudeDelta / 2 &&
          Math.abs(sortie.lon - vue.center.longitude) <= vue.span.longitudeDelta / 2,
      )
      .map((sortie) => sortie.id);
    const signature = ids.join('|');
    if (signature === vueDite.current) return;
    vueDite.current = signature;
    actuel.onVue(ids);
  }

  /*
    Les pastilles seules, sans toucher au cadrage.

    Appelee aussi a chaque fin de mouvement : c'est la que les groupes se
    defont ou se reforment. Surtout pas le cadrage — la carte se
    recadrerait a la fin de son propre recadrage, sans fin.
  */
  function poserLesReperes() {
    const pret = carte.current;
    if (!pret) return;
    const { mapkit, carte: instance } = pret;
    const actuel = derniers.current;

    /*
      Ce qui tient dans la vue, dit avant tout le reste.

      Avant la sortie anticipee sur la signature des groupes : deplacer
      un peu la carte peut faire sortir une sortie par le bord sans rien
      changer aux groupes, et le compte du tiroir doit quand meme suivre.
    */
    dire(actuel);
    poserMonPoint();

    const groupes = grouper(actuel);
    const signature = groupes
      .map(({ sorties: dedans }) => dedans.map((sortie) => sortie.id).join('+'))
      .join('|');
    if (signature === dessine.current) return;
    dessine.current = signature;

    instance.removeAnnotations(reperes.current);
    reperes.current = groupes.map(({ lieu, sorties: dedans }) => {
      const seule = dedans.length === 1 ? dedans[0]! : null;
      const ouvrir = () => {
        pilotee.current = true;
        ouvertA.current = Date.now();
        if (seule) choisir.current(seule);
        else grouperLes.current(dedans);
      };
      const annotation = new mapkit.Annotation(
        new mapkit.Coordinate(lieu.lat, lieu.lon),
        () => (seule ? pastilleDe(seule, ouvrir) : pastilleDeGroupe(dedans.length, ouvrir)),
        {
          /*
            La punaise, seule ou de groupe, se pose par la pointe de son
            aiguille : le bas de l'element, sans decalage. Sa taille est dite
            a MapKit plutot que mesuree, pour que le bas soit bien la pointe.
          */
          anchorOffset: new DOMPoint(0, 0),
          size: seule ? { width: 20, height: 46 } : { width: 28, height: 54 },
          calloutEnabled: false,
          /* Les pastilles paraissent et disparaissent en douceur : elles
             sont refaites a chaque fois que les groupes changent, et un
             changement sec donnait l'impression d'une autre image. */
          animates: true,
          title: seule ? seule.title : `${dedans.length} sorties`,
        },
      );
      annotation.addEventListener('select', () => {
        ouvrir();
        /* Relachee, pour qu'un second appui la rouvre. */
        instance.selectedAnnotation = null;
      });
      return annotation;
    });
    instance.addAnnotations(reperes.current);
  }

  /**
   * Mon point, pose a part des sorties.
   *
   * Il se posait a la suite des pastilles, apres la sortie anticipee sur
   * la signature des groupes. Or la position arrive souvent apres la
   * carte : a son arrivee les groupes n'avaient pas bouge, la fonction
   * s'arretait avant lui, et le point n'apparaissait qu'une fois les
   * groupes refaits — en dezoomant beaucoup. Il ne depend donc plus que
   * de lui-meme, et ne se refait que s'il a bouge.
   */
  function poserMonPoint() {
    const pret = carte.current;
    if (!pret) return;
    const { mapkit, carte: instance } = pret;
    const moi = derniers.current.moi;
    const deja = monRepere.current?.coordinate;
    if (moi && deja && deja.latitude === moi.lat && deja.longitude === moi.lon) return;

    if (monRepere.current) instance.removeAnnotation(monRepere.current);
    monRepere.current = null;
    if (!moi) return;
    monRepere.current = new mapkit.Annotation(new mapkit.Coordinate(moi.lat, moi.lon), rondDeMoi, {
      /*
        Le centre du rond sur ma position. MapKit pose un repere par le bas
        de son element, et un decalage positif le fait MONTER : +12 le
        hissait d'une vingtaine de pixels au-dessus de moi, et les punaises
        semblaient a cote alors que c'est moi qui l'etais. -9, la moitie
        de ses dix-huit pixels, le centre.
      */
      anchorOffset: new DOMPoint(0, -9),
      size: { width: 18, height: 18 },
      calloutEnabled: false,
      enabled: false,
      animates: false,
    });
    instance.addAnnotation(monRepere.current);
  }

  /**
   * Les reperes et le cadrage, a partir de ce qu'on sait maintenant.
   * Appele a la creation et a chaque changement : la carte peut arriver
   * apres les donnees, ou avant.
   */
  function poser() {
    const pret = carte.current;
    if (!pret) return;
    const { mapkit, carte: instance } = pret;
    const actuel = derniers.current;

    poserLesReperes();

    if (pilotee.current) return;

    /*
      Un seul mouvement possible apres l'ouverture : la ville cede la
      place a ma position, quand elle finit par arriver. Rien d'autre ne
      deplace la vue tout seul — surtout pas une sortie qui se charge.
    */
    const voulu = surQuoiSOuvrir(actuel);
    if (voulu === 'rien' || voulu === cadrageFait.current) return;
    if (voulu === 'ville' && cadrageFait.current === 'moi') return;
    cadrageFait.current = voulu;

    bouger(() =>
      instance.setRegionAnimated(
        voulu === 'moi'
          ? region(mapkit, actuel.moi!, ETENDUE_MOI)
          : region(mapkit, actuel.centreVille!, actuel.etendueVille ?? ETENDUE_VILLE),
        false,
      ),
    );
  }

  /* L'ecouteur pose a la creation appelle par ce relais : la fonction,
     elle, change a chaque rendu. */
  reposer.current = poserLesReperes;

  useEffect(poser, [sorties, moi, centreVille, props.moiDansLaVille]);

  /* Le jour et la nuit changent le fond, pas les reperes. */
  useEffect(() => {
    const pret = carte.current;
    if (!pret) return;
    pret.carte.colorScheme = nuit ? pret.mapkit.Map.ColorSchemes.Dark : pret.mapkit.Map.ColorSchemes.Light;
  }, [nuit]);

  useImperativeHandle(poignee, () => ({
    recentrer: () => {
      const pret = carte.current;
      const point = derniers.current.moi;
      if (!pret || !point) return;
      pret.carte.setRegionAnimated(region(pret.mapkit, point, ETENDUE_MOI), true);
    },
    viser: (lieu, partCachee = 0) => {
      const pret = carte.current;
      if (!pret) return;
      /* Choisir dans le tiroir, c'est prendre la main : la carte ne doit
         plus se recadrer toute seule ensuite. */
      pilotee.current = true;
      /*
        Le point se pose au-dessus de la fiche, pas dessous.

        La fiche couvre le bas de la carte : centrer sur le point le
        mettait derriere elle. On descend donc le centre de la moitie de
        ce qu'elle cache, et le point remonte d'autant — il se retrouve
        au milieu de ce qui reste visible.
      */
      const part = Math.min(Math.max(partCachee, 0), 0.7);
      const centre = { lat: lieu.lat - (ETENDUE_PRES * part) / 2, lon: lieu.lon };
      bouger(() => pret.carte.setRegionAnimated(region(pret.mapkit, centre, ETENDUE_PRES), true));
    },
    approcher: (lieux) => {
      const pret = carte.current;
      if (!pret || lieux.length === 0) return;
      /* Aller voir, c'est prendre la main : plus de recadrage tout seul. */
      pilotee.current = true;
      const lats = lieux.map((lieu) => lieu.lat);
      const lons = lieux.map((lieu) => lieu.lon);
      const centre = {
        lat: (Math.min(...lats) + Math.max(...lats)) / 2,
        lon: (Math.min(...lons) + Math.max(...lons)) / 2,
      };
      const etendue = Math.max(
        (Math.max(...lats) - Math.min(...lats)) * MARGE_GROUPE,
        (Math.max(...lons) - Math.min(...lons)) * MARGE_GROUPE,
        ETENDUE_PRES,
      );
      bouger(() => pret.carte.setRegionAnimated(region(pret.mapkit, centre, etendue), true));
    },
  }));

  return <div ref={zone} className="ev-carte__fond" />;
}
